import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Sun } from 'lucide-react';

// Big Bang Launch — 08/08/2026 at 08:08:08 UTC (Lion's Gate Portal · Lucky 8 · Manifestation)
export const LAUNCH_TIMESTAMP = Date.UTC(2026, 7, 8, 8, 8, 8); // months are 0-indexed (7 = August)

function pad(n) {
    return String(n).padStart(2, '0');
}

function computeDiff(target) {
    const diff = Math.max(0, target - Date.now());
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds, total: diff };
}

export function CountdownToLaunch({ compact = false, accent = 'gold' }) {
    const { t } = useTranslation();
    const [diff, setDiff] = useState(() => computeDiff(LAUNCH_TIMESTAMP));

    useEffect(() => {
        const id = setInterval(() => setDiff(computeDiff(LAUNCH_TIMESTAMP)), 1000);
        return () => clearInterval(id);
    }, []);

    if (diff.total <= 0) {
        return (
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#00FF66]/10 border border-[#00FF66]/30">
                <Sun className="w-4 h-4 text-[#00FF66] animate-pulse" />
                <span className="text-[#00FF66] font-bold text-sm uppercase tracking-widest">
                    {t('countdown.live')}
                </span>
            </div>
        );
    }

    const cellBase = accent === 'gold'
        ? 'bg-[#FFD700]/10 border-[#FFD700]/30 text-[#FFD700]'
        : 'bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]';

    if (compact) {
        return (
            <div className="inline-flex items-center gap-2 font-mono text-sm text-white/80" data-testid="countdown-compact">
                <Sparkles className="w-3.5 h-3.5 text-[#FFD700]" />
                <span className={accent === 'gold' ? 'text-[#FFD700]' : 'text-[#00FF66]'}>
                    {diff.days}{t('countdown.dShort')} {pad(diff.hours)}:{pad(diff.minutes)}:{pad(diff.seconds)}
                </span>
                <span className="text-white/40 hidden sm:inline">· 08.08.26</span>
            </div>
        );
    }

    const cells = [
        { value: diff.days, labelKey: 'countdown.days' },
        { value: diff.hours, labelKey: 'countdown.hours' },
        { value: diff.minutes, labelKey: 'countdown.minutes' },
        { value: diff.seconds, labelKey: 'countdown.seconds' },
    ];

    return (
        <div className="w-full" data-testid="countdown-full">
            <div className="flex items-center justify-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-[#FFD700]" />
                <span className="text-[#FFD700] text-xs font-bold uppercase tracking-[0.3em]">
                    {t('countdown.bigBang')}
                </span>
                <Sparkles className="w-4 h-4 text-[#FFD700]" />
            </div>
            <div className="grid grid-cols-4 gap-3 max-w-2xl mx-auto">
                {cells.map((c) => (
                    <div
                        key={c.labelKey}
                        className={`text-center p-4 rounded-xl border ${cellBase} backdrop-blur`}
                    >
                        <div className="font-mono text-4xl sm:text-5xl font-black tabular-nums leading-none">
                            {pad(c.value)}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-white/50 mt-2 font-semibold">
                            {t(c.labelKey)}
                        </div>
                    </div>
                ))}
            </div>
            <div className="text-center mt-4 text-xs text-white/40 font-mono tracking-wider">
                08.08.2026 · 08:08:08 UTC · {t('countdown.lionsGate')}
            </div>
        </div>
    );
}
